import {useState} from "react";
import {Box, Button, Stack, Typography} from "@mui/material";

/**
 * Start button behavior depends on the screen size we have.
 *
 * in-frame: on a bigger screen we swap the current view for the tour where it stands. 
 * own-page: on a small (mobile) screen we navigate to our own page for full view.
 */
export type StartTour =
    | {target: "in-frame"; onStart: () => void}
    | {target: "own-page"; href: string};

const CONTENT_MAX_WIDTH_PX = 420;
const SECONDARY_TEXT_OPACITY = 0.85;
const TOUR_HINT = "Swipe to look around · Tap a room to move";

// Keeps the text legible over whatever room is behind it.
const PICTURE_SCRIM = "linear-gradient(to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,0.85) 100%)";

// Button props are left untyped on purpose because MUI ButtonProps gives us unwanted style constraints. 
const START_BUTTON_PROPS = {
    variant: "contained", size: "large", fullWidth: true, sx: {mt: 3, textTransform: "none"}
} as const;

export interface EntryScreenProps {
    title: string;
    roomCount: number;
    /** Room image candidates tried in order until one loads. */
    pictureUrls?: string[];
    startTour: StartTour;
}

/**
 * The first thing a visitor sees when the viewer is embedded in a host page:
 *
 * - Tour information (title, room count)
 * - Picture of the room it starts in
 * - Button to start the actual tour
 */
export function EntryScreen(props: EntryScreenProps) {
    const {title, roomCount, pictureUrls = [], startTour} = props;

    // A DisplayName on a viewpoint doesn't guarantee a room image was exported for it, so we step
    // past any URL that 404s and use the first that actually loads.
    const [index, setIndex] = useState(0);
    const pictureUrl = pictureUrls[index];

    const summary = `3D tour · ${roomCount} ${roomCount === 1 ? "room" : "rooms"}`;

    return <Box sx={{position: "absolute", inset: 0, overflow: "hidden", backgroundColor: "common.black"}}>

        {pictureUrl && <Box component={"img"} src={pictureUrl} alt={""}
                            onError={() => setIndex(i => i + 1)}
                            sx={{position: "absolute", inset: 0, width: "100%", height: "100%",
                                objectFit: "cover"}}/>}

        <Stack justifyContent={"flex-end"} alignItems={"center"}
               sx={{position: "absolute", inset: 0, background: PICTURE_SCRIM, p: 3, color: "common.white"}}>

            <Box sx={{width: "100%", maxWidth: CONTENT_MAX_WIDTH_PX, textAlign: "center"}}>
                <Typography variant={"h5"} component={"h1"}>{title}</Typography>
                <Typography variant={"body2"} sx={{opacity: SECONDARY_TEXT_OPACITY}}>{summary}</Typography>

                {startTour.target === "own-page"
                    ? <Button {...START_BUTTON_PROPS} component={"a"} href={startTour.href} target={"_top"}>
                        Start tour
                    </Button>
                    : <Button {...START_BUTTON_PROPS} onClick={startTour.onStart}>
                        Start tour
                    </Button>}

                <Typography variant={"caption"} component={"p"}
                            sx={{mt: 2, opacity: SECONDARY_TEXT_OPACITY}}>{TOUR_HINT}</Typography>
            </Box>
        </Stack>
    </Box>;
}
